import React, { useState, useRef } from 'react';
import { AutoComplete, Input, Typography } from 'antd';
import { useDebouncedCallback } from 'use-debounce';
import { useApolloClient } from '@apollo/client';
import { GET_PLACE_PREDICTIONS, GET_PLACE_DETAILS } from 'api/places/queries';
import {
  GetPlaceDetails,
  GetPlaceDetails_getPlaceDetails,
  GetPlaceDetailsVariables,
} from 'api/places/types/GetPlaceDetails';
import {
  GetPlacePredictions,
  GetPlacePredictions_getPlacePredictions_entities,
  GetPlacePredictionsVariables,
} from 'api/places/types/GetPlacePredictions';
import { uniqueId } from 'lodash';
// Styles
import styles from './PlaceInput.module.scss';

export interface PlaceInputProps {
  value?: string;
  name?: string;
  disableBrowserAutocomplete?: boolean;
  autocompleteBlockerPrefix?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  onChange?: (value: string) => void;
  onSearchChange?: (value: string) => void;
  onPlaceSelect?: (placeDetails: GetPlaceDetails_getPlaceDetails) => void;
}

const PlaceInput: React.FC<PlaceInputProps> = ({
  value = '',
  name,
  disableBrowserAutocomplete = false,
  autocompleteBlockerPrefix,
  label,
  placeholder = 'Enter location...',
  disabled = false,
  error,
  onChange,
  onSearchChange,
  onPlaceSelect,
}) => {
  const [options, setOptions] = useState<
    Array<{
      value: string;
      key: string;
      entity: GetPlacePredictions_getPlacePredictions_entities;
    }>
  >([]);
  // Using a session token to group requests
  const sessionTokenRef = useRef<string>(uniqueId());

  const client = useApolloClient();

  // Debounced function to fetch place predictions when the user types
  const debouncedInputChange = useDebouncedCallback(async (query: string) => {
    if (!query.trim()) {
      setOptions([]);
      return;
    }
    try {
      const { data } = await client.query<
        GetPlacePredictions,
        GetPlacePredictionsVariables
      >({
        query: GET_PLACE_PREDICTIONS,
        variables: {
          input: {
            query,
            sessionToken: sessionTokenRef.current,
          },
        },
        fetchPolicy: 'network-only',
      });
      const entities = data?.getPlacePredictions?.entities || [];
      const newOptions = entities.map((entity) => ({
        value: entity.text, // display text in the dropdown
        key: entity.id,
        entity,
      }));
      setOptions(newOptions);
    } catch (err) {
      console.error('Error fetching predictions:', err);
      setOptions([]);
    }
  }, 1000);

  const handleSearch = (searchText: string) => {
    if (onChange) onChange(searchText);
    if (onSearchChange) onSearchChange(searchText);
    debouncedInputChange(searchText);
  };

  // Called when a suggestion is selected.
  const handleSelect = async (selectedValue: string, option: any) => {
    // option.entity is the original entity we attached
    const entity: GetPlacePredictions_getPlacePredictions_entities =
      option.entity;
    if (entity) {
      try {
        const { data } = await client.query<
          GetPlaceDetails,
          GetPlaceDetailsVariables
        >({
          query: GET_PLACE_DETAILS,
          variables: {
            input: {
              id: entity.id,
              sessionToken: sessionTokenRef.current,
            },
          },
          fetchPolicy: 'network-only',
        });
        const placeDetails = data?.getPlaceDetails;
        if (placeDetails && onPlaceSelect) {
          onPlaceSelect(placeDetails);
        }
      } catch (err) {
        console.error('Error fetching place details:', err);
        setOptions([]);
      }
    }
    // Update the input value with the selected suggestion
    if (onChange) onChange(selectedValue);
    sessionTokenRef.current = uniqueId();
  };

  return (
    <div>
      {label && <Typography.Text strong>{label}</Typography.Text>}
      {disableBrowserAutocomplete && autocompleteBlockerPrefix && (
        <div aria-hidden className={styles.autofillBlocker}>
          <input
            autoComplete="off"
            name={`${autocompleteBlockerPrefix}-text`}
            tabIndex={-1}
            type="text"
          />
          <input
            autoComplete="new-password"
            name={`${autocompleteBlockerPrefix}-password`}
            tabIndex={-1}
            type="password"
          />
        </div>
      )}
      <AutoComplete
        value={value}
        onSearch={handleSearch}
        onSelect={handleSelect}
        options={options}
        disabled={disabled}
        placeholder={placeholder}
        className={styles.autoComplete}
      >
        <Input
          autoComplete={disableBrowserAutocomplete ? 'off' : 'new-password'}
          name={name}
        />
      </AutoComplete>
      {error && <Typography.Text type="danger">{error}</Typography.Text>}
    </div>
  );
};

export default PlaceInput;
